// Validate network data before it reaches amCharts' date/timezone formatter.
export function normalizeHistory(rows) {
  if (!Array.isArray(rows)) throw new Error('Invalid candle history response');
  const byTime = new Map();
  for (const row of rows) {
    if (row?.time == null || row.time === '') continue;
    const DateValue = new Date(row.time).getTime();
    const candle = { Date: DateValue, RealDate: new Date(DateValue),
      Open: parseFloat(row.open), High: parseFloat(row.high),
      Low: parseFloat(row.low), Close: parseFloat(row.close),
      Volume: validVolume(row.volume, row.volumeAvailable) };
    if (Number.isFinite(DateValue) && validOHLC(candle)) byTime.set(DateValue, candle);
  }
  return [...byTime.values()].sort((a, b) => a.Date - b.Date);
}

export function validVolume(value, available) {
  if (available === false || value == null || value === '') return null;
  const volume = Number(value);
  return Number.isFinite(volume) && volume >= 0 ? volume : null;
}

export function validOHLC(row) {
  return ['Open', 'High', 'Low', 'Close'].every(key => Number.isFinite(row[key]))
    && row.High >= Math.max(row.Open, row.Close) && row.Low <= Math.min(row.Open, row.Close);
}

// The live quote stream may only contain the part of a candle seen since the
// subscription began. Retain the historical open/extrema while merging it.
export function mergeLiveCandle(previous, tick, intervalMs) {
  const row = {
    Date: tick?.t == null || tick.t === '' ? NaN : new Date(tick.t).getTime(),
    Open: Number.parseFloat(tick?.o), High: Number.parseFloat(tick?.h),
    Low: Number.parseFloat(tick?.l), Close: Number.parseFloat(tick?.c),
    Volume: validVolume(tick?.v, tick?.volumeAvailable),
    LiveObserved: true,
  };
  if (!Number.isFinite(row.Date) || !validOHLC(row) || !Number.isFinite(intervalMs)
      || intervalMs <= 0 || row.Date % intervalMs !== 0) return null;
  if (!previous) return row;
  if (row.Date < previous.Date) return null;
  if (row.Date === previous.Date && tick.ohlcComplete !== true) {
    row.Open = previous.Open;
    row.High = Math.max(previous.High, row.High);
    row.Low = Math.min(previous.Low, row.Low);
  }
  return row;
}

export function sameCandle(left, right) {
  return !!left && !!right && ['Date', 'Open', 'High', 'Low', 'Close', 'Volume']
    .every(key => Object.is(left[key], right[key]));
}

// Live rows can increase current.length without moving the historical boundary.
export function olderCandles(history, current, count) {
  if (!current.length || count <= 0) return [];
  const first = current[0].Date;
  let low = 0, high = history.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (history[middle].Date < first) low = middle + 1;
    else high = middle;
  }
  return history.slice(Math.max(0, low - count), low);
}

// Repair old off-timeframe rows too; inserting canonical rows alone leaves the
// invalid 17:40 row on a 15m chart forever. Legitimate missing-market gaps stay.
export function removeMisalignedCandles(list, intervalMs, anchor = 0) {
  if (!Number.isFinite(intervalMs) || intervalMs <= 0) return;
  for (let index = list.length - 1; index >= 0; index--) {
    if ((list.getIndex(index).Date - anchor) % intervalMs !== 0) list.removeIndex(index);
  }
}

// Retain loaded history; refresh known candles and insert missed ones without
// clearing the gapless axis's date list during an active axis animation.
export function reconcileCandles(list, rows) {
  let index = 0;
  for (const row of rows) {
    while (index < list.length && list.getIndex(index).Date < row.Date) index++;
    const current = list.getIndex(index);
    if (current?.Date === row.Date) {
      if (['Open', 'High', 'Low', 'Close', 'Volume'].some(k => current[k] !== row[k])) list.setIndex(index, row);
    } else if (index === list.length) list.push(row);
    else list.insertIndex(index, row);
    index++;
  }
}

// History is already account-markup adjusted by the API and is authoritative
// for completed/missed candles. LiveObserved only means we saw part of a bar;
// it must never exempt that bar from correction.
// Recovery must not eagerly load older, intentionally paged-out history.
export function selectRecoveryRows(rows, firstDate, liveTail, tailAtRequest, currentRows = []) {
  const changed = new Map();
  if (tailAtRequest) {
    for (const current of currentRows) {
      if (current.Date >= tailAtRequest.Date && current !== tailAtRequest) changed.set(current.Date, current);
    }
  }
  if (liveTail && liveTail !== tailAtRequest) changed.set(liveTail.Date, liveTail);
  return rows.filter(row => row.Date >= firstDate).map(row => {
    const live = changed.get(row.Date);
    if (!live) return row;
    // A newer close must survive an in-flight HTTP response, but missing
    // extrema/open from before reconnection must still be repaired. Protect
    // every bar updated during this request, even if another bar has opened.
    return { ...row, Open: row.Open, Close: live.Close,
      High: Math.max(row.High, live.Close,
        live.Date !== tailAtRequest?.Date || live.High > tailAtRequest.High ? live.High : -Infinity),
      Low: Math.min(row.Low, live.Close,
        live.Date !== tailAtRequest?.Date || live.Low < tailAtRequest.Low ? live.Low : Infinity),
      Volume: live.Volume, ...(live.LiveObserved ? {LiveObserved: true} : {}) };
  });
}
